/**
 * AkteAufgabenCard — Aufgaben-Modul innerhalb einer Akte.
 *
 * Zeigt die zur Akte gehörenden Aufgaben (akte_id) als kompakte Liste mit
 * Frist, zuständigem Mitarbeiter und Status. Über "+ Aufgabe" bzw. Klick auf
 * eine Zeile öffnet sich rechts ein Detail-Drawer zum Anlegen/Bearbeiten —
 * analog zum Aufgaben-Bereich. Neu erstellte Aufgaben sind über akte_id
 * automatisch mit der Akte verknüpft und erscheinen auch im Aufgaben-Modul.
 *
 * Funktioniert betriebsübergreifend (gutachten, autoglas, mietwagen, werkstatt)
 * — die business_unit wird als Prop übergeben.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { CheckSquare, Plus, X, Check, Loader2, CalendarDays, Trash2, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAufgabenCRUD, type AufgabeMitMitarbeiter } from '@/hooks/useAufgabenCRUD';
import { statusLabel, prioLabel, istUeberfaellig, initialen, formatFrist } from '@/lib/aufgaben-format';

export interface AkteAufgabenCardProps {
  akteId: string;
  businessUnit: string;
  akteLabel?: string;
}

const PRIO_DOT: Record<string, string> = {
  dringend: 'bg-red-600',
  hoch: 'bg-red-500',
  mittel: 'bg-amber-500',
  niedrig: 'bg-emerald-500',
};

const PRIO_RANK: Record<string, number> = { dringend: 3, hoch: 2, mittel: 1, niedrig: 0 };

const STATUS_BADGE: Record<string, string> = {
  offen: 'bg-gray-100 text-gray-600',
  in_bearbeitung: 'bg-blue-50 text-blue-600',
  erledigt: 'bg-emerald-50 text-emerald-700',
  abgebrochen: 'bg-gray-100 text-gray-400',
};

const inputCls =
  'w-full border border-gray-300 rounded-lg px-2.5 py-2 text-[13px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100';

function istErledigt(status: string | null | undefined): boolean {
  return status === 'erledigt' || status === 'abgebrochen';
}

/** Offene zuerst, davon überfällige oben, dann nach Frist, dann Priorität; erledigte ans Ende. */
function sortAufgaben(list: AufgabeMitMitarbeiter[]): AufgabeMitMitarbeiter[] {
  return [...list].sort((a, b) => {
    const doneA = istErledigt(a.status) ? 1 : 0;
    const doneB = istErledigt(b.status) ? 1 : 0;
    if (doneA !== doneB) return doneA - doneB;
    if (doneA === 0) {
      const lateA = istUeberfaellig(a.faellig_am, a.status) ? 0 : 1;
      const lateB = istUeberfaellig(b.faellig_am, b.status) ? 0 : 1;
      if (lateA !== lateB) return lateA - lateB;
      const fa = a.faellig_am || '';
      const fb = b.faellig_am || '';
      if (!!fa !== !!fb) return fa ? -1 : 1;
      if (fa && fb && fa !== fb) return fa < fb ? -1 : 1;
      return (PRIO_RANK[b.prioritaet || 'mittel'] || 0) - (PRIO_RANK[a.prioritaet || 'mittel'] || 0);
    }
    return 0;
  });
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">{label}</span>
      {children}
    </label>
  );
}

export default function AkteAufgabenCard({ akteId, businessUnit, akteLabel }: AkteAufgabenCardProps) {
  const { profile, teamMembers } = useAuth();
  const { aufgaben, createAufgabe, updateAufgabe, toggleStatus, deleteAufgabe, pendingStatusIds } = useAufgabenCRUD();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<AufgabeMitMitarbeiter | null>(null);
  const [saving, setSaving] = useState(false);

  const [fTitel, setFTitel] = useState('');
  const [fStatus, setFStatus] = useState('offen');
  const [fPrio, setFPrio] = useState('mittel');
  const [fFrist, setFFrist] = useState('');
  const [fAssignee, setFAssignee] = useState('');
  const [fBeschreibung, setFBeschreibung] = useState('');

  // Mitarbeiter-Liste kommt aus dem bereits geladenen Team (kein extra Query).
  const mitarbeiter = useMemo(
    () =>
      teamMembers
        .filter((m) => m.ist_aktiv !== false)
        .map((m) => ({ id: m.id, vorname: m.vorname, nachname: m.nachname })),
    [teamMembers],
  );

  const akteAufgaben = useMemo(
    () => sortAufgaben(aufgaben.filter((a) => a.akte_id === akteId)),
    [aufgaben, akteId],
  );
  const offeneAnzahl = akteAufgaben.filter((a) => !istErledigt(a.status)).length;

  // Drawer mit Escape schließen.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  function openCreate() {
    setEditing(null);
    setFTitel('');
    setFStatus('offen');
    setFPrio('mittel');
    setFFrist('');
    setFAssignee('');
    setFBeschreibung('');
    setDrawerOpen(true);
  }

  function openEdit(a: AufgabeMitMitarbeiter) {
    setEditing(a);
    setFTitel(a.titel || '');
    setFStatus(a.status || 'offen');
    setFPrio(a.prioritaet || 'mittel');
    setFFrist(a.faellig_am ? a.faellig_am.slice(0, 10) : '');
    setFAssignee(a.zugewiesen_an || '');
    setFBeschreibung(a.beschreibung || '');
    setDrawerOpen(true);
  }

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      const titel = fTitel.trim() || 'Unbenannte Aufgabe';
      if (editing) {
        await updateAufgabe(editing.id, {
          titel,
          status: fStatus,
          prioritaet: fPrio,
          faellig_am: fFrist || null,
          zugewiesen_an: fAssignee || null,
          beschreibung: fBeschreibung || undefined,
        });
      } else {
        const neu = await createAufgabe({
          titel,
          business_unit: businessUnit,
          akte_id: akteId,
          prioritaet: fPrio,
          faellig_am: fFrist || undefined,
          zugewiesen_an: fAssignee || undefined,
          erstellt_von: profile?.id || undefined,
          beschreibung: fBeschreibung || undefined,
        });
        // createAufgabe legt immer mit Status "offen" an — abweichenden Status nachziehen.
        if (neu && fStatus !== 'offen') {
          await updateAufgabe(neu.id, { status: fStatus });
        }
      }
      setDrawerOpen(false);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!editing) return;
    const ok = await deleteAufgabe(editing.id);
    if (ok) setDrawerOpen(false);
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
        <CheckSquare className="w-4 h-4 text-gray-500" />
        <span className="text-[13px] font-semibold text-gray-800">Aufgaben</span>
        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
          {offeneAnzahl} offen
        </span>
        <button
          type="button"
          onClick={openCreate}
          data-testid="akte-aufgabe-neu"
          className="ml-auto inline-flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Aufgabe
        </button>
      </div>

      {akteAufgaben.length === 0 ? (
        <div className="px-4 py-6 text-center text-[12px] text-gray-400">Noch keine Aufgaben für diese Akte.</div>
      ) : (
        <div className="divide-y divide-gray-50">
          {akteAufgaben.map((a) => {
            const late = istUeberfaellig(a.faellig_am, a.status);
            const done = a.status === 'erledigt';
            const pending = pendingStatusIds.has(a.id);
            return (
              <div
                key={a.id}
                onClick={() => openEdit(a)}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleStatus(a.id, a.status);
                  }}
                  disabled={pending}
                  aria-label={done ? 'Als offen markieren' : 'Als erledigt markieren'}
                  className={[
                    'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                    done ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-gray-300 hover:border-emerald-400',
                  ].join(' ')}
                >
                  {pending ? (
                    <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                  ) : done ? (
                    <Check className="w-3 h-3 text-white" />
                  ) : null}
                </button>
                <div className="min-w-0 flex-1">
                  <div
                    className={[
                      'text-[13px] font-medium truncate',
                      done ? 'line-through text-gray-400' : 'text-gray-800',
                    ].join(' ')}
                  >
                    {a.titel}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-500 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <span className={['w-1.5 h-1.5 rounded-full', PRIO_DOT[a.prioritaet || 'mittel'] || 'bg-amber-500'].join(' ')} />
                      {prioLabel(a.prioritaet)}
                    </span>
                    <span className={['inline-flex items-center gap-1', late ? 'text-red-600 font-semibold' : ''].join(' ')}>
                      <CalendarDays className="w-3 h-3" />
                      {formatFrist(a.faellig_am)}
                      {late ? ' · überfällig' : ''}
                    </span>
                    {a.mitarbeiter && (
                      <span className="inline-flex items-center gap-1">
                        <span className="w-5 h-5 rounded-full bg-gray-200 text-gray-600 text-[9px] font-bold flex items-center justify-center">
                          {initialen(a.mitarbeiter.vorname, a.mitarbeiter.nachname)}
                        </span>
                        {a.mitarbeiter.vorname} {a.mitarbeiter.nachname}
                      </span>
                    )}
                  </div>
                </div>
                <span
                  className={[
                    'text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0',
                    STATUS_BADGE[a.status || 'offen'] || 'bg-gray-100 text-gray-600',
                  ].join(' ')}
                >
                  {statusLabel(a.status)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {drawerOpen && (
        <>
          <div className="fixed inset-0 bg-black/30 z-[80]" onClick={() => setDrawerOpen(false)} />
          <div
            data-testid="akte-aufgabe-drawer"
            className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[81] flex flex-col"
          >
            <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
              <span className="text-[10px] font-bold uppercase tracking-wide text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                Aufgabe
              </span>
              <span className="text-[12px] text-gray-400 font-medium">{editing ? 'Bearbeiten' : 'Neu'}</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="ml-auto w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <input
                value={fTitel}
                onChange={(e) => setFTitel(e.target.value)}
                placeholder="Aufgabentitel …"
                autoFocus
                className="w-full text-[16px] font-semibold outline-none placeholder:text-gray-300"
              />

              <div className="grid grid-cols-2 gap-3">
                <Field label="Status">
                  <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={inputCls}>
                    <option value="offen">Offen</option>
                    <option value="in_bearbeitung">In Bearbeitung</option>
                    <option value="erledigt">Erledigt</option>
                    <option value="abgebrochen">Abgebrochen</option>
                  </select>
                </Field>
                <Field label="Priorität">
                  <select value={fPrio} onChange={(e) => setFPrio(e.target.value)} className={inputCls}>
                    <option value="dringend">Dringend</option>
                    <option value="hoch">Hoch</option>
                    <option value="mittel">Mittel</option>
                    <option value="niedrig">Niedrig</option>
                  </select>
                </Field>
                <Field label="Frist">
                  <input type="date" value={fFrist} onChange={(e) => setFFrist(e.target.value)} className={inputCls} />
                </Field>
                <Field label="Zuständiger Mitarbeiter">
                  <select value={fAssignee} onChange={(e) => setFAssignee(e.target.value)} className={inputCls}>
                    <option value="">— Niemand —</option>
                    {mitarbeiter.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.vorname} {m.nachname}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Beschreibung">
                <textarea
                  value={fBeschreibung}
                  onChange={(e) => setFBeschreibung(e.target.value)}
                  rows={4}
                  placeholder="Details zur Aufgabe …"
                  className={`${inputCls} resize-y`}
                />
              </Field>

              {akteLabel && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">Verknüpfte Akte</div>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-[12px] text-gray-700">
                    <ExternalLink className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                    {akteLabel}
                  </div>
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-gray-100 flex items-center gap-2">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-400"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Speichern
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="text-[13px] font-medium px-3 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                Abbrechen
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={remove}
                  className="ml-auto inline-flex items-center gap-1.5 text-[13px] font-medium px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Löschen
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
